import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Modal, Toast, InputGroup } from 'react-bootstrap';
import Api from '../Auth/Api';
import { decodeJWT } from '../Components/jwtUtils';

function SuperAdmins() {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const idUsuarioLogado = decodeJWT(token)?.idUsuario ?? null;

    const [superAdmins, setSuperAdmins] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showConfirmRemover, setShowConfirmRemover] = useState({ show: false, admin: null });
    const [showSenha, setShowSenha] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toast, setToast] = useState({});

    // Modo manutenção
    const [manutencao, setManutencao] = useState(false);
    const [showConfirmManutencao, setShowConfirmManutencao] = useState(false);
    const [alterandoManutencao, setAlterandoManutencao] = useState(false);

    const { register, handleSubmit, reset, formState: { errors } } = useForm();

    const fetchSuperAdmins = useCallback(async () => {
        try {
            const response = await Api.get('api/super-admins', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuperAdmins(response.data);
        } catch (error) {
            console.error(error?.response?.data?.message || error);
        }
    }, [token]);

    const fetchManutencao = useCallback(async () => {
        try {
            const response = await Api.get('api/manutencao', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setManutencao(response.data.ativo === true);
        } catch (error) {
            console.error(error?.response?.data?.message || error);
        }
    }, [token]);

    useEffect(() => {
        fetchSuperAdmins();
        fetchManutencao();
    }, [fetchSuperAdmins, fetchManutencao]);

    const alterarManutencao = async (ativo) => {
        setAlterandoManutencao(true);
        try {
            const response = await Api.post('api/manutencao', { ativo }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setManutencao(response.data.ativo === true);
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
        } catch (error) {
            setToast({ header: 'Ops!', body: error?.response?.data?.message || 'Erro ao alterar o modo manutenção.', type: 'toast-error' });
        }
        setAlterandoManutencao(false);
        setShowConfirmManutencao(false);
        setShowToast(true);
    };

    const handleToggleManutencao = () => {
        if (!manutencao) {
            // Ativar bloqueia usuários — pede confirmação
            setShowConfirmManutencao(true);
        } else {
            alterarManutencao(false);
        }
    };

    const abrirNovo = () => {
        reset({ nome_completo: '', codigo: '', email: '', senha: '' });
        setShowSenha(false);
        setShowModal(true);
    };

    const onSubmit = async (data) => {
        try {
            const response = await Api.post('api/super-admins', data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            setShowModal(false);
            fetchSuperAdmins();
        } catch (error) {
            setToast({ header: 'Ops!', body: error?.response?.data?.message || 'Erro ao criar Super Admin.', type: 'toast-error' });
        }
        setShowToast(true);
    };

    const removerSuperAdmin = async () => {
        const { admin } = showConfirmRemover;
        setShowConfirmRemover({ show: false, admin: null });
        try {
            const response = await Api.delete(`api/super-admins/${admin.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            fetchSuperAdmins();
        } catch (error) {
            setToast({ header: 'Ops!', body: error?.response?.data?.message || 'Erro ao remover Super Admin.', type: 'toast-error' });
        }
        setShowToast(true);
    };

    return (
        <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center mb-4 gap-2">
                <div>
                    <h4 className="mb-0 fw-bold"><i className="bi bi-shield-lock me-2 text-primary" />Super Admins</h4>
                    <small className="text-muted">Contas com acesso total à plataforma e controles globais</small>
                </div>
                <button className="btn btn-primary flex-shrink-0" onClick={abrirNovo}>
                    <i className="bi bi-plus-lg me-sm-1" /><span className="d-none d-sm-inline">Novo Super Admin</span>
                </button>
            </div>

            {/* Card de modo manutenção */}
            <div className={`card border-0 shadow-sm mb-4 ${manutencao ? 'border-start border-4 border-warning' : ''}`}>
                <div className="card-body d-flex align-items-center justify-content-between gap-2 gap-md-3 p-3 p-md-4">
                    <div className="d-flex align-items-center gap-2 gap-md-3 min-w-0 flex-grow-1">
                        <div className="rounded-3 p-2 p-md-3 flex-shrink-0 d-none d-sm-block" style={{ background: manutencao ? '#fff4e0' : '#f5f5f5' }}>
                            <i className={`bi bi-cone-striped fs-3 ${manutencao ? 'text-warning' : 'text-secondary'}`} />
                        </div>
                        <div className="min-w-0">
                            <div className="fw-semibold">
                                Modo manutenção
                                {manutencao && <span className="badge bg-warning text-dark ms-2">Ativo</span>}
                            </div>
                            <small className="text-muted">
                                {manutencao
                                    ? 'O login está bloqueado para todos os usuários. Apenas administradores conseguem entrar.'
                                    : 'Ao ativar, apenas administradores conseguirão fazer login na plataforma.'}
                            </small>
                        </div>
                    </div>
                    <div className="form-check form-switch fs-4 mb-0 flex-shrink-0">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="switchManutencao"
                            checked={manutencao}
                            disabled={alterandoManutencao}
                            onChange={handleToggleManutencao}
                        />
                    </div>
                </div>
            </div>

            {/* Tabela de super admins */}
            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table align-middle mb-0 table-striped table-hover">
                            <thead>
                                <tr>
                                    <th colSpan="4" className="bg-primary text-white rounded-top">Lista de Super Admins</th>
                                </tr>
                                <tr className="border border-white text-nowrap">
                                    <th className="text-uppercase text-black-50 fw-bold fs-8 bg-muted">Nome</th>
                                    <th className="text-uppercase text-black-50 fw-bold fs-8 bg-muted">Matrícula</th>
                                    <th className="text-uppercase text-black-50 fw-bold fs-8 bg-muted d-none d-md-table-cell">E-mail</th>
                                    <th className="text-uppercase text-black-50 fw-bold fs-8 bg-muted text-end pe-4">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {superAdmins.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center text-muted py-5">
                                            <i className="bi bi-shield-slash fs-2 d-block mb-2" />
                                            Nenhum Super Admin cadastrado.
                                        </td>
                                    </tr>
                                )}
                                {superAdmins.map((admin) => (
                                    <tr key={admin.id}>
                                        <td>
                                            <span className="fw-semibold">{admin.nome_completo}</span>
                                            {String(admin.id) === String(idUsuarioLogado) && (
                                                <span className="badge bg-info ms-2">Você</span>
                                            )}
                                        </td>
                                        <td className="text-muted">{admin.codigo}</td>
                                        <td className="text-muted d-none d-md-table-cell">{admin.email || '-'}</td>
                                        <td className="text-end pe-3">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger text-nowrap"
                                                disabled={String(admin.id) === String(idUsuarioLogado)}
                                                title={String(admin.id) === String(idUsuarioLogado) ? 'Você não pode remover a própria conta' : 'Remover'}
                                                onClick={() => setShowConfirmRemover({ show: true, admin })}
                                            >
                                                <i className="bi bi-trash me-sm-1" /><span className="d-none d-sm-inline">Remover</span>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th colSpan="4" className="bg-primary text-white rounded-bottom">
                                        <div className="table-footer d-flex justify-content-between align-items-center">
                                            <span>{superAdmins.length} super admin{superAdmins.length !== 1 ? 's' : ''}</span>
                                        </div>
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal criar super admin */}
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" size="lg">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Header closeButton>
                        <span className="fw-semibold fs-5 text-primary">Novo Super Admin</span>
                    </Modal.Header>
                    <Modal.Body className="bg-gray-100 p-4">
                        <div className="container">
                            <div className="alert alert-warning">
                                <i className="bi bi-exclamation-triangle me-2" />
                                Super Admins têm acesso total: gerenciam todas as empresas, configurações globais e outros super admins.
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">Nome completo *</label>
                                    <input
                                        className={`form-control border-new-light ${errors.nome_completo ? 'is-invalid' : ''}`}
                                        placeholder="Nome do super admin"
                                        {...register('nome_completo', { required: 'O nome é obrigatório.' })}
                                    />
                                    {errors.nome_completo && <div className="invalid-feedback">{errors.nome_completo.message}</div>}
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">Matrícula *</label>
                                    <input
                                        className={`form-control border-new-light ${errors.codigo ? 'is-invalid' : ''}`}
                                        placeholder="Login de acesso"
                                        {...register('codigo', { required: 'A matrícula é obrigatória.' })}
                                    />
                                    {errors.codigo && <div className="invalid-feedback">{errors.codigo.message}</div>}
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">E-mail *</label>
                                    <input
                                        type="email"
                                        className={`form-control border-new-light ${errors.email ? 'is-invalid' : ''}`}
                                        placeholder="email@empresa.com"
                                        {...register('email', {
                                            required: 'O e-mail é obrigatório.',
                                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido.' }
                                        })}
                                    />
                                    {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label text-primary fw-medium">Senha *</label>
                                    <InputGroup>
                                        <input
                                            type={showSenha ? 'text' : 'password'}
                                            className={`form-control border-new-light ${errors.senha ? 'is-invalid' : ''}`}
                                            placeholder="Mínimo 6 caracteres"
                                            {...register('senha', {
                                                required: 'A senha é obrigatória.',
                                                minLength: { value: 6, message: 'A senha deve ter pelo menos 6 caracteres.' }
                                            })}
                                        />
                                        <button type="button" className="btn btn-outline-secondary"
                                            onClick={() => setShowSenha(v => !v)}>
                                            <i className={`bi bi-eye${showSenha ? '-slash' : ''}`} />
                                        </button>
                                        {errors.senha && <div className="invalid-feedback">{errors.senha.message}</div>}
                                    </InputGroup>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-end">
                        <div className="d-flex justify-content-end">
                            <button type="button" className="btn me-2" onClick={() => setShowModal(false)}>
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary">Criar Super Admin</button>
                        </div>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* Modal confirmar remoção */}
            <Modal show={showConfirmRemover.show} onHide={() => setShowConfirmRemover({ show: false, admin: null })} centered>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-danger">Remover Super Admin</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <p className="mb-0">
                        Tem certeza que deseja remover o Super Admin{' '}
                        <strong>"{showConfirmRemover.admin?.nome_completo}"</strong>{' '}
                        (matrícula {showConfirmRemover.admin?.codigo})?
                        <br />
                        <span className="text-muted small">Esta conta perderá imediatamente o acesso à plataforma.</span>
                    </p>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn me-2"
                            onClick={() => setShowConfirmRemover({ show: false, admin: null })}>
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-danger" onClick={removerSuperAdmin}>
                            Remover
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Modal confirmar ativação da manutenção */}
            <Modal show={showConfirmManutencao} onHide={() => setShowConfirmManutencao(false)} centered>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-warning">
                        <i className="bi bi-cone-striped me-2" />Ativar modo manutenção
                    </span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <p className="mb-0">
                        Ao ativar o modo manutenção, <strong>nenhum aluno, gestor ou RH conseguirá fazer login</strong> em
                        nenhuma empresa da plataforma. Apenas administradores terão acesso.
                        <br /><br />
                        Deseja continuar?
                    </p>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn me-2" onClick={() => setShowConfirmManutencao(false)}>
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-warning" disabled={alterandoManutencao}
                            onClick={() => alterarManutencao(true)}>
                            {alterandoManutencao ? 'Ativando...' : 'Ativar manutenção'}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={toast.type}
                style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}
            >
                <Toast.Header><strong className="me-auto">{toast.header}</strong></Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>
        </div>
    );
}

export default SuperAdmins;
